import React, { useCallback, useEffect, useRef, useState } from 'react';
import { injectIntl, IntlShape } from 'react-intl';

import './async-search-input.scss';

import { INPUT_DEBOUNCE_TIME } from '../../../constants/common.constants';
import { debounce } from '../../../infrastructure/utils/debounce.utils';

export enum ButtonType { Blue, Black }

interface SearchInputProps {
    value?: string;
    intl: IntlShape;
    disabled?: boolean;
    onEnterButtonDown?: (v: string) => void;
    withDebounce?: boolean;
    className?: string;
    onChange?: (v: string) => void;
    placeholder?: string;
    onKeyDown?: (e: React.KeyboardEvent<HTMLInputElement>) => void;
    autoFocus?: boolean;
}

const AsyncSearchInput = injectIntl((props: SearchInputProps) => {
    const {
        value,
        className,
        disabled,
        onChange = () => null,
        onEnterButtonDown = () => null,
        withDebounce = false,
        autoFocus = false,
        ...otherProps
    } = props;

    const [inputValue, setInputValue] = useState('');
    const inputRef = useRef(null);
    const onChangeCallback = useCallback(withDebounce ? debounce(onChange, INPUT_DEBOUNCE_TIME) : onChange, []);
    const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const newValue = e.currentTarget.value;
        setInputValue(newValue);
        onChangeCallback(newValue);

        if (!newValue?.length) {
            setInputValue('');
        }
    };
    const handleReset = () => {
        setInputValue('');
        onChange('');
    };
    const handleKeyDown = e => {
        if (e.key === 'Enter') {
            onEnterButtonDown(inputValue);
        }
    };

    useEffect(() => {
        if (disabled) handleReset();
    }, [disabled]);

    return (
        <>
            <input
                type='text'
                className={className}
                disabled={disabled}
                value={value || inputValue}
                onChange={handleChange}
                onKeyDown={handleKeyDown}
                ref={inputRef}
                autoFocus={autoFocus}
                {...otherProps}
            />
        </>
    );
});

export { AsyncSearchInput };
